import Box from '@mui/material/Box/Box'
import Skeleton from '@mui/material/Skeleton/Skeleton'
import useTheme from '@mui/material/styles/useTheme'
import Typography from '@mui/material/Typography/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { Spacer } from 'components-ui/src/spacer/spacer'
import type { Order } from 'components-ui/src/table/table.d'
import { useClientLocationServiceType } from 'service-api/src/hooks/use_client_location_service_type'
import { useClientWaitListForUser } from 'service-api/src/hooks/use_client_waitlist'
import type { WaitlistOrderClientDto } from 'service-api/src/types/api'
import {
  defaultSearchParams,
  getVisibleWaitList,
  isWaitlistSortField,
  mergeWaitListPage,
  WaitlistSortField,
  type TWaitlistSort,
} from './helper'
import { Toolbar } from './toolbar'
import { WaitListTableLup } from './waitlist_table'

export const WaitlistRecords = () => {
  const { accountName: locationName = '' } = useParams()
  const sessionCtx = useSessionContext()
  const theme = useTheme()
  const isBelowMD = useMediaQuery(theme.breakpoints.down('md'))
  const [searchParams, setSearchParams] = useState(() => defaultSearchParams())
  const [waitList, setWaitList] = useState<WaitlistOrderClientDto[]>([])
  const [sort, setSort] = useState<TWaitlistSort>({
    field: WaitlistSortField.SERVICE_DATES,
    order: 'desc',
  })

  const { data: serviceTypes, isLoading: serviceTypesIsLoading } = useClientLocationServiceType(locationName)
  const { data: waitListData, isLoading: waitlistIsLoading } = useClientWaitListForUser(
    locationName,
    sessionCtx.state.user?.lupId || '',
    {
      serviceTypeName: searchParams.service !== 'all' ? searchParams.service : undefined,
      waitlistStatus: searchParams.waitlistStatus !== 'all' ? searchParams.waitlistStatus : undefined,
      limit: searchParams.limit,
      nextToken: searchParams.nextToken || undefined,
      includes: [
        'location',
        'locationUserProfile',
        'pets.locationPetProfile.breed',
        'primaryServiceType.serviceType',
        'invoices.items.offer',
        'invoices.petRelations',
      ],
    }
  )

  useEffect(() => {
    if (!waitListData?.results) return

    setWaitList((accumulated) => mergeWaitListPage(accumulated, waitListData.results, searchParams.nextToken))
  }, [searchParams.nextToken, waitListData?.results])

  const columnSortHandler = useCallback((field: string, order: Order) => {
    if (!isWaitlistSortField(field)) return
    setSort({ field, order })
  }, [])

  const onFilterHandler = useCallback((params: Partial<typeof searchParams>) => {
    setWaitList([])
    setSearchParams((prev) => ({ ...prev, ...params, nextToken: null }))
  }, [])

  const visibleWaitList = useMemo(() => {
    return getVisibleWaitList(waitList, sort)
  }, [sort, waitList])

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h2" sx={{ mb: 2 }} pl={isBelowMD ? 2 : 0}>
        {t('profile.waitlist.title')}
      </Typography>

      {serviceTypesIsLoading ? (
        <>
          <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 3 }} />
          <Skeleton variant="rectangular" width="100%" height={40} sx={{ mb: 3 }} />
        </>
      ) : (
        <>
          <Toolbar onFilterParamsChange={onFilterHandler} serviceTypes={serviceTypes?.results || []} />
          <Spacer height={3} />
          <WaitListTableLup
            isLoading={waitlistIsLoading}
            nextToken={waitListData?.nextToken}
            onLoadMore={(limit, nextToken) => {
              if (nextToken) {
                setSearchParams((prev) => ({ ...prev, limit, nextToken }))
              }
            }}
            waitList={visibleWaitList}
            onColumnSort={columnSortHandler}
            serviceTypes={serviceTypes?.results || []}
          />
        </>
      )}
    </Box>
  )
}
